import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, RefreshCcw, ShieldCheck, AlertTriangle, Globe, Trash2, ExternalLink } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import DomainFormDialog from "./domains/DomainFormDialog";
import { DomainStatusBadge, SslStatusBadge, ProviderBadge } from "./domains/DomainBadges";

function SummaryCard({ label, value, tone = "slate", testid }) {
  const tones = {
    slate: "border-white/10 bg-white/5 text-white",
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-200",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-200",
    rose: "border-rose-500/30 bg-rose-500/10 text-rose-200",
    cyan: "border-cyan-500/30 bg-cyan-500/10 text-cyan-200",
    violet: "border-violet-500/30 bg-violet-500/10 text-violet-200",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 font-bold">{label}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

export default function PlatformDomains() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [providers, setProviders] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dnsProv, setDnsProv] = useState("");
  const [sslProv, setSslProv] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [openForm, setOpenForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ls, prv, tns] = await Promise.all([
        api.get("/platform/domains", { params: {
          q: q || undefined, dns_provider: dnsProv || undefined,
          ssl_provider: sslProv || undefined, status: statusFilter || undefined,
        }}),
        api.get("/platform/domains/providers"),
        api.get("/platform/tenants"),
      ]);
      setItems(ls.data.items || []);
      setSummary(ls.data.summary || {});
      setProviders(prv.data);
      setTenants(tns.data.items || []);
    } catch (e) { notifyError(e); }
    finally { setLoading(false); }
  }, [q, dnsProv, sslProv, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const tenantName = (tid) => (tenants.find(t => t.tenant_id === tid) || {}).tenant_name || tid?.slice(0, 8);

  const del = async (d) => {
    if (!window.confirm(`Hapus domain ${d.fqdn}?`)) return;
    try {
      await api.delete(`/platform/domains/${d.id}`);
      toast.success("Domain dihapus.");
      load();
    } catch (e) { notifyError(e, "Gagal hapus"); }
  };

  return (
    <PlatformLayout title="Domain (DNS & SSL)">
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="text-white font-display font-black text-xl">Domain Management</div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="dom-refresh">
            <RefreshCcw size={14} className="mr-1" /> Refresh
          </Button>
          <Button onClick={() => setOpenForm(true)} className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:opacity-90 text-white font-black" data-testid="dom-add-btn">
            <Plus size={14} className="mr-1" /> Tambah Domain
          </Button>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-7 mb-4">
          <SummaryCard label="Total" value={summary.total ?? 0} tone="cyan" testid="dom-sum-total" />
          <SummaryCard label="Active" value={summary.active ?? 0} tone="emerald" testid="dom-sum-active" />
          <SummaryCard label="Pending" value={summary.pending ?? 0} tone="slate" testid="dom-sum-pending" />
          <SummaryCard label="Verifying" value={summary.verifying ?? 0} tone="amber" testid="dom-sum-verifying" />
          <SummaryCard label="DNS Error" value={summary.dns_error ?? 0} tone="rose" testid="dom-sum-dns-error" />
          <SummaryCard label="SSL Error" value={summary.ssl_error ?? 0} tone="rose" testid="dom-sum-ssl-error" />
          <SummaryCard label="Disabled" value={summary.disabled ?? 0} tone="violet" testid="dom-sum-disabled" />
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari domain / subdomain…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-cyan-500/60"
              data-testid="dom-search" />
          </div>
          <select value={dnsProv} onChange={(e) => setDnsProv(e.target.value)}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" data-testid="dom-filter-dns">
            <option value="">Semua DNS Provider</option>
            {providers?.dns?.map(p => <option key={p.code} value={p.code}>{p.label}{!p.ready ? " (stub)" : ""}</option>)}
          </select>
          <select value={sslProv} onChange={(e) => setSslProv(e.target.value)}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" data-testid="dom-filter-ssl">
            <option value="">Semua SSL Provider</option>
            {providers?.ssl?.map(p => <option key={p.code} value={p.code}>{p.label}{!p.ready ? " (stub)" : ""}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" data-testid="dom-filter-status">
            <option value="">Semua Status</option>
            {["PENDING","VERIFYING","ACTIVE","DNS_ERROR","SSL_ERROR","DISABLED"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
        <table className="w-full text-sm" data-testid="dom-table">
          <thead className="bg-white/5 border-b border-white/10">
            <tr className="text-left text-[10px] uppercase tracking-widest text-white/50">
              <th className="px-4 py-3 font-bold">FQDN</th>
              <th className="px-4 py-3 font-bold">Tenant</th>
              <th className="px-4 py-3 font-bold">DNS Provider</th>
              <th className="px-4 py-3 font-bold">SSL Provider</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold">SSL</th>
              <th className="px-4 py-3 font-bold text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-white/40 text-center">Memuat…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-white/40 text-center" data-testid="dom-empty">Belum ada domain.</td></tr>
            )}
            {!loading && items.map((d) => (
              <tr key={d.id} className="border-b border-white/5 hover:bg-white/5 transition" data-testid={`dom-row-${d.id}`}>
                <td className="px-4 py-3 font-mono text-white">{d.fqdn}</td>
                <td className="px-4 py-3 text-white/80">{tenantName(d.tenant_id)}</td>
                <td className="px-4 py-3"><ProviderBadge code={d.dns_provider} providers={providers?.dns} kind="dns" /></td>
                <td className="px-4 py-3"><ProviderBadge code={d.ssl_provider} providers={providers?.ssl} kind="ssl" /></td>
                <td className="px-4 py-3"><DomainStatusBadge status={d.status} /></td>
                <td className="px-4 py-3"><SslStatusBadge status={d.ssl_status} /></td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => nav(`/platform/domains/${d.id}`)}
                      className="text-white/60 hover:text-white hover:bg-white/10" data-testid={`dom-view-${d.id}`}>
                      <ExternalLink size={12} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(d)}
                      className="text-rose-300 hover:bg-rose-500/10" data-testid={`dom-delete-${d.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openForm && providers && (
        <DomainFormDialog
          providers={providers}
          tenants={tenants}
          onClose={() => setOpenForm(false)}
          onSuccess={(created) => { setOpenForm(false); load(); if (created?.id) nav(`/platform/domains/${created.id}`); }}
        />
      )}
    </PlatformLayout>
  );
}
